/**
 * Multi-Artifact Project Store
 *
 * Replaces the old skill-centric editorStore with project-based state management.
 * Supports projects containing multiple artifacts (skills, subflows, integrations, attributes).
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api, rotateProjectSuffix as rotateProjectSuffixApi } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

export type ArtifactType = 'SKILL' | 'SUBSKILL' | 'SUBFLOW' | 'INTEGRATION' | 'ATTRIBUTE' | 'FACET';
export type ProjectStatus = 'WORKBENCH' | 'LIBRARY';

// Project-level stages (Master Pipeline)
export type ProjectStageType = 'PLAN' | 'LUMASCRIPT' | 'BPMN' | 'ARTIFACT_DESIGN';

// Artifact-level stages (Individual Pipelines)
export type ArtifactStageType =
  | 'PROMPT'
  | 'ARTIFACT_PLAN'
  | 'LUMASCRIPT'
  | 'BPMN'
  | 'LUMA_SKILL'
  | 'LUMAJSON';

// Combined type for UI compatibility (will be deprecated)
export type StageType = ProjectStageType | ArtifactStageType;

export type ValidationStatus = 'VALID' | 'INVALID' | 'WARNING' | 'PENDING' | null;

export interface ValidationResult {
  confidence: number;
  status: ValidationStatus;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  passed: string[];
  validatedAt: string;
}

export interface ValidationIssue {
  rule: string;
  severity: 'error' | 'warning';
  message: string;
  location?: string;
  suggestion?: string;
}

/**
 * Project Stage - Project-level transformation pipeline stages
 * Master Pipeline: PLAN → LUMASCRIPT → BPMN → ARTIFACT_DESIGN
 */
export interface ProjectStage {
  stage: ProjectStageType;
  content: string;
  isDirty: boolean;
  updatedAt: string;
}

/**
 * Artifact Stage - Artifact-level transformation pipeline stages
 * Individual Pipeline: ARTIFACT_PLAN → LUMASCRIPT → BPMN → LUMA_SKILL/LUMAJSON
 */
export interface ArtifactStage {
  stage: ArtifactStageType;
  content: string;
  isDirty: boolean;
  updatedAt: string;
}

export interface ProjectArtifact {
  id: string;
  type: ArtifactType;
  identifier: string;           // Base identifier (without suffix)
  fullIdentifier: string;        // identifier_suffix (e.g., "main_skill_111A")
  name: string;
  description?: string;

  // Seed Plan - Original plan fragment from Master Plan that seeded this artifact
  seedPlan: string | null;

  // Each artifact has its OWN transformation pipeline
  stages: {
    [K in ArtifactStageType]?: ArtifactStage;
  };

  lumaJson: any | null;          // Luma proprietary JSON (final output)

  // Dual-source version tracking
  lumascriptVersion?: number;    // Version of LumaScript representation
  lumajsonVersion?: number;      // Version of LumaJSON representation
  activeSource?: 'lumascript' | 'lumajson';  // Which source is primary
  modifiedBy?: string;           // Who last modified (AI/User)
  lastModified?: number;         // Timestamp of last modification

  // Phase 6: Prompt Metadata - Track artifact generation history
  generatedFromProject?: boolean;           // Was this artifact created from project-level generation?
  projectGenerationDate?: number;           // Timestamp when generated from project
  originalUserInstruction?: string | null;  // Simple user instruction from project generation
  originalFullPrompt?: string | null;       // Full LLM prompt from project generation
  userModifiedPrompt?: boolean;             // Has user edited the prompt since generation?
  promptLastModified?: number;              // Timestamp of last prompt modification

  validation: ValidationResult | null;
  createdAt: string;
  updatedAt: string;
}

export interface Project {
  id: string;
  ownerId?: string;
  ownerName?: string;
  name: string;
  description?: string;
  agentPrompt?: string;          // Initial user prompt that started the project
  suffix: string;                // Shared suffix (e.g., "111A")
  status: ProjectStatus;
  isInbox?: boolean;
  isReadOnly?: boolean;
  artifactsLoaded?: boolean;
  stagesLoaded?: boolean;

  // Project-level stages (Master Pipeline)
  // PLAN → LUMASCRIPT → BPMN → ARTIFACT_DESIGN
  stages: {
    [K in ProjectStageType]?: ProjectStage;
  };

  // Project artifacts (skills, subflows, integrations, attributes)
  // Each artifact has its own transformation pipeline via artifact.stages
  artifacts: {
    [artifactId: string]: ProjectArtifact;
  };

  // Metadata
  metadata: {
    createdAt: string;
    updatedAt: string;
    lastValidatedAt?: string;
  };
}

export interface ProjectStoreState {
  // Active project
  activeProjectId: string | null;

  // Projects (keyed by project ID)
  projects: {
    [projectId: string]: Project;
  };
  libraryProjects: {
    [projectId: string]: Project;
  };

  // UI state
  ui: {
    activeStage: StageType;
    selectedArtifactId: string | null;
    explorerGroupBy: 'type' | 'none';
    isValidating: boolean;
    isSaving: boolean;
  };

  // Project Actions
  loadAllProjects: () => Promise<void>;
  loadProject: (projectId: string) => Promise<void>;
  loadLibraryProjects: () => Promise<void>;
  createProject: (name: string, description?: string, agentPrompt?: string) => Promise<string>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  setActiveProject: (projectId: string | null) => void;

  // Stage Actions
  setActiveStage: (stage: StageType) => void;

  // Project-Level Stage Actions (Master Pipeline)
  updateProjectStage: (projectId: string, stage: ProjectStageType, content: string, isDirty: boolean) => Promise<void>;
  getProjectStage: (projectId: string, stage: ProjectStageType) => ProjectStage | null;

  // Artifact Actions
  loadArtifacts: (projectId: string) => Promise<void>;
  createArtifact: (
    projectId: string,
    type: ArtifactType,
    identifier: string,
    name: string,
    description?: string,
    seedPlan?: string,
    promptMetadata?: {
      generatedFromProject?: boolean;
      originalUserInstruction?: string;
      originalFullPrompt?: string;
    }
  ) => Promise<string>;
  updateArtifact: (projectId: string, artifactId: string, updates: Partial<ProjectArtifact>) => Promise<void>;
  updateArtifactStage: (projectId: string, artifactId: string, stage: StageType, content: string, isDirty: boolean) => Promise<void>;
  deleteArtifact: (projectId: string, artifactId: string) => Promise<void>;
  setSelectedArtifact: (artifactId: string | null) => void;

  // Validation Actions
  validateProject: (projectId: string) => Promise<ValidationResult>;
  validateArtifact: (projectId: string, artifactId: string) => Promise<ValidationResult>;

  // Library Actions
  saveToLibrary: (projectId: string) => Promise<void>;
  copyToWorkbench: (projectId: string) => Promise<string>;

  // Utility
  getActiveProject: () => Project | null;
  getSelectedArtifact: () => ProjectArtifact | null;
  getArtifactsByType: (projectId: string, type: ArtifactType) => ProjectArtifact[];

  // UI Actions
  setExplorerGroupBy: (groupBy: 'type' | 'none') => void;
  resetStore: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  activeProjectId: null,
  projects: {},
  libraryProjects: {},
  ui: {
    activeStage: 'PROMPT' as StageType,
    selectedArtifactId: null,
    explorerGroupBy: 'type' as const,
    isValidating: false,
    isSaving: false
  }
};

// ============================================================================
// Store
// ============================================================================

export const useProjectStore = create<ProjectStoreState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ======================================================================
      // Project Actions
      // ======================================================================

      loadAllProjects: async () => {
        try {
          const response = await api.get('/projects');
          const { projects: projectList } = response.data;

          set((state) => {
            const projectsMap: { [id: string]: Project } = {};
            for (const proj of projectList) {
              const existingProject = state.projects[proj.id];
              projectsMap[proj.id] = {
                id: proj.id,
                ownerId: proj.userId,
                ownerName: proj.ownerName || undefined,
                name: proj.name,
                description: proj.description,
                agentPrompt: proj.agentPrompt,
                suffix: proj.suffix,
                isInbox: Boolean((proj as any).isInbox ?? (proj as any).is_inbox),
                status: proj.status,
                stages: existingProject?.stages || {},
                artifacts: existingProject?.artifacts || {},
                artifactsLoaded: existingProject?.artifactsLoaded ?? false,
                stagesLoaded: existingProject?.stagesLoaded ?? false,
                metadata: {
                  createdAt: new Date(proj.createdAt).toISOString(),
                  updatedAt: new Date(proj.updatedAt).toISOString()
                }
              };
            }

            return { projects: projectsMap };
          });

          await get().loadLibraryProjects();
        } catch (error) {
          console.error('[projectStore] Failed to load projects:', error);
          throw error;
        }
      },

      loadLibraryProjects: async () => {
        try {
          const response = await api.get('/projects/library');
          const { projects: projectList } = response.data;
          const map: { [id: string]: Project } = {};
          for (const proj of projectList) {
            map[proj.id] = {
              id: proj.id,
              ownerId: proj.userId,
              ownerName: proj.ownerName || 'Shared',
              name: proj.name,
              description: proj.description,
              agentPrompt: proj.agentPrompt,
              suffix: proj.suffix,
              status: 'LIBRARY',
              isInbox: Boolean((proj as any).isInbox ?? (proj as any).is_inbox),
              isReadOnly: true,
              stages: {},
              artifacts: {},
              metadata: {
                createdAt: new Date(proj.createdAt).toISOString(),
                updatedAt: new Date(proj.updatedAt).toISOString()
              }
            };
          }
          set({ libraryProjects: map });
        } catch (error) {
          console.error('[projectStore] Failed to load library projects:', error);
        }
      },

      loadProject: async (projectId: string) => {
        try {
          const response = await api.get(`/projects/${projectId}`);
          const { project } = response.data;

          // Transform artifacts array to object
          const artifactsMap: { [id: string]: ProjectArtifact } = {};
          for (const artifact of project.artifacts || []) {
            artifactsMap[artifact.id] = {
              id: artifact.id,
              type: artifact.type,
              identifier: artifact.identifier,
              fullIdentifier: artifact.fullIdentifier,
              name: artifact.name,
              description: artifact.description,
              seedPlan: artifact.seedPlan || null,  // Original plan fragment from master
              stages: artifact.stages || {},  // Artifact transformation stages
              lumaJson: artifact.lumaJson,
              lumascriptVersion: artifact.lumascriptVersion || 0,
              lumajsonVersion: artifact.lumajsonVersion || 0,
              generatedFromProject: artifact.generatedFromProject || false,
              projectGenerationDate: artifact.projectGenerationDate || null,
              originalUserInstruction: artifact.originalUserInstruction || null,
              originalFullPrompt: artifact.originalFullPrompt || null,
              userModifiedPrompt: artifact.userModifiedPrompt || false,
              promptLastModified: artifact.promptLastModified || null,
              validation: artifact.validationResult,
              createdAt: new Date(artifact.createdAt).toISOString(),
              updatedAt: new Date(artifact.updatedAt).toISOString()
            };
          }

          // Transform project-level stages (Master Pipeline)
          const projectStages: { [K in ProjectStageType]?: ProjectStage } = {};
          if (project.stages) {
            for (const stage of project.stages) {
              projectStages[stage.stage as ProjectStageType] = {
                stage: stage.stage as ProjectStageType,
                content: stage.content,
                isDirty: Boolean(stage.isDirty),
                updatedAt: new Date(stage.updatedAt).toISOString()
              };
            }
          }

          const loadedProject: Project = {
            id: project.id,
            name: project.name,
            description: project.description,
            agentPrompt: project.agentPrompt,
            suffix: project.suffix,
            status: project.status,
            isInbox: Boolean((project as any).isInbox ?? (project as any).is_inbox),
            stages: projectStages,  // Project-level stages (Master Pipeline)
            artifacts: artifactsMap,  // Artifact-level stages (Individual Pipelines)
            artifactsLoaded: true,
            stagesLoaded: true,
            metadata: {
              createdAt: new Date(project.createdAt).toISOString(),
              updatedAt: new Date(project.updatedAt).toISOString()
            }
          };

          console.log('[loadProject] Loaded project with stages:', {
            projectId,
            stageCount: Object.keys(projectStages).length,
            stages: Object.keys(projectStages),
            hasLumascript: !!projectStages.LUMASCRIPT,
            lumascriptLength: projectStages.LUMASCRIPT?.content?.length || 0
          });

          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: loadedProject
            },
            activeProjectId: projectId
          }));
        } catch (error) {
          console.error('Failed to load project:', error);
          throw error;
        }
      },

      createProject: async (name: string, description?: string, agentPrompt?: string) => {
        try {
          const response = await api.post('/projects', { name, description, agentPrompt });
          const { project } = response.data;

          const newProject: Project = {
            id: project.id,
            name: project.name,
            description: project.description,
            agentPrompt: project.agentPrompt,
            suffix: project.suffix,
            status: 'WORKBENCH',
            isInbox: Boolean((project as any).isInbox ?? (project as any).is_inbox),
            stages: {},
            artifacts: {},
            artifactsLoaded: false,
            stagesLoaded: false,
            metadata: {
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          };

          set((state) => ({
            projects: {
              ...state.projects,
              [project.id]: newProject
            },
            activeProjectId: project.id
          }));

          return project.id;
        } catch (error) {
          console.error('Failed to create project:', error);
          throw error;
        }
      },

      updateProject: async (projectId: string, updates: Partial<Project>) => {
        try {
          await api.put(`/projects/${projectId}`, updates);

          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...state.projects[projectId],
                ...updates,
                metadata: {
                  ...state.projects[projectId]?.metadata,
                  updatedAt: new Date().toISOString()
                }
              }
            }
          }));
        } catch (error) {
          console.error('Failed to update project:', error);
          throw error;
        }
      },

      deleteProject: async (projectId: string) => {
        try {
          await api.delete(`/projects/${projectId}`);

          set((state) => {
            const { [projectId]: removed, ...remainingProjects } = state.projects;
            return {
              projects: remainingProjects,
              activeProjectId: state.activeProjectId === projectId ? null : state.activeProjectId
            };
          });
        } catch (error) {
          console.error('Failed to delete project:', error);
          throw error;
        }
      },

      rotateProjectSuffix: async (projectId: string) => {
        const response = await rotateProjectSuffixApi(projectId);
        await get().loadProject(projectId);
        return response.suffix;
      },

      setActiveProject: (projectId: string | null) => {
        set((state) => {
          // When the project stays the same but an artifact is selected, allow switching back to project context
          if (state.activeProjectId === projectId) {
            if (state.ui.selectedArtifactId !== null) {
              return {
                ui: {
                  ...state.ui,
                  selectedArtifactId: null
                }
              };
            }
            return {};
          }

          return {
            activeProjectId: projectId,
            ui: {
              ...state.ui,
              selectedArtifactId: null
            }
          };
        });
      },

      // ======================================================================
      // Stage Actions
      // ======================================================================

      setActiveStage: (stage: StageType) => {
        set((state) => ({
          ui: {
            ...state.ui,
            activeStage: stage
          }
        }));
      },

      // ======================================================================
      // Project-Level Stage Actions (Master Pipeline)
      // ======================================================================

      updateProjectStage: async (projectId: string, stage: ProjectStageType, content: string, isDirty: boolean) => {
        try {
          // Call API to persist
          await api.put(`/projects/${projectId}/stages/${stage}`, { content, isDirty });

          // Update local state
          const now = new Date().toISOString();
          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...state.projects[projectId],
                stages: {
                  ...state.projects[projectId]?.stages,
                  [stage]: {
                    stage,
                    content,
                    isDirty,
                    updatedAt: now
                  }
                }
              }
            }
          }));
        } catch (error) {
          console.error('Failed to update project stage:', error);
          throw error;
        }
      },

      getProjectStage: (projectId: string, stage: ProjectStageType) => {
        const state = get();
        return state.projects[projectId]?.stages[stage] || null;
      },

      // ======================================================================
      // Artifact Actions
      // ======================================================================

      loadArtifacts: async (projectId: string) => {
        try {
          const response = await api.get(`/projects/${projectId}/artifacts`);
          const { artifacts } = response.data;

          const artifactsMap: { [id: string]: ProjectArtifact } = {};
          for (const artifact of artifacts || []) {
            artifactsMap[artifact.id] = {
              id: artifact.id,
              type: artifact.type,
              identifier: artifact.identifier,
              fullIdentifier: artifact.fullIdentifier,
              name: artifact.name,
              description: artifact.description,
              seedPlan: artifact.seedPlan || null,  // Original plan fragment from master
              stages: artifact.stages || {},  // Artifact transformation stages
              lumaJson: artifact.lumaJson,
              lumascriptVersion: artifact.lumascriptVersion || 0,
              lumajsonVersion: artifact.lumajsonVersion || 0,
              generatedFromProject: artifact.generatedFromProject || false,
              projectGenerationDate: artifact.projectGenerationDate || null,
              originalUserInstruction: artifact.originalUserInstruction || null,
              originalFullPrompt: artifact.originalFullPrompt || null,
              userModifiedPrompt: artifact.userModifiedPrompt || false,
              promptLastModified: artifact.promptLastModified || null,
              validation: artifact.validationResult,
              createdAt: new Date(artifact.createdAt).toISOString(),
              updatedAt: new Date(artifact.updatedAt).toISOString()
            };
          }

          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...state.projects[projectId],
                artifacts: artifactsMap,
                artifactsLoaded: true
              }
            }
          }));
        } catch (error) {
          console.error('Failed to load artifacts:', error);
          throw error;
        }
      },

      createArtifact: async (
        projectId: string,
        type: ArtifactType,
        identifier: string,
        name: string,
        description?: string,
        seedPlan?: string,
        promptMetadata?: {
          generatedFromProject?: boolean;
          originalUserInstruction?: string;
          originalFullPrompt?: string;
        }
      ) => {
        try {
          const response = await api.post(`/projects/${projectId}/artifacts`, {
            type,
            identifier,
            name,
            description,
            seedPlan,
            // Phase 6: Prompt metadata
            generatedFromProject: promptMetadata?.generatedFromProject,
            originalUserInstruction: promptMetadata?.originalUserInstruction,
            originalFullPrompt: promptMetadata?.originalFullPrompt
          });
          const { artifact } = response.data;

          const newArtifact: ProjectArtifact = {
            id: artifact.id,
            type: artifact.type,
            identifier: artifact.identifier,
            fullIdentifier: artifact.fullIdentifier,
            name: artifact.name,
            description: artifact.description,
            seedPlan: artifact.seedPlan || null,  // Original plan fragment from master
            stages: artifact.stages || {}, // Artifact transformation stages
            lumaJson: artifact.lumaJson || null,
            // Phase 6: Prompt metadata
            generatedFromProject: artifact.generatedFromProject,
            projectGenerationDate: artifact.projectGenerationDate,
            originalUserInstruction: artifact.originalUserInstruction,
            originalFullPrompt: artifact.originalFullPrompt,
            userModifiedPrompt: artifact.userModifiedPrompt,
            promptLastModified: artifact.promptLastModified,
            validation: null,
            createdAt: new Date(artifact.createdAt).toISOString(),
            updatedAt: new Date(artifact.updatedAt).toISOString()
          };

          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...state.projects[projectId],
                artifacts: {
                  ...state.projects[projectId]?.artifacts,
                  [artifact.id]: newArtifact
                }
              }
            },
            ui: {
              ...state.ui,
              selectedArtifactId: artifact.id
            }
          }));

          return artifact.id;
        } catch (error) {
          console.error('Failed to create artifact:', error);
          throw error;
        }
      },

      updateArtifact: async (projectId: string, artifactId: string, updates: Partial<ProjectArtifact>) => {
        try {
          await api.put(`/projects/${projectId}/artifacts/${artifactId}`, updates);

          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...state.projects[projectId],
                artifacts: {
                  ...state.projects[projectId]?.artifacts,
                  [artifactId]: {
                    ...state.projects[projectId]?.artifacts?.[artifactId],
                    ...updates,
                    updatedAt: new Date().toISOString()
                  }
                }
              }
            }
          }));
        } catch (error) {
          console.error('Failed to update artifact:', error);
          throw error;
        }
      },

      updateArtifactStage: async (projectId: string, artifactId: string, stage: StageType, content: string, isDirty: boolean) => {
        try {
          console.log('[updateArtifactStage] Saving stage', { projectId, artifactId, stage, contentLength: content?.length || 0 });
          await api.put(`/projects/${projectId}/artifacts/${artifactId}/stages/${stage}`, {
            content,
            isDirty
          });

          // Check if artifact exists in store, if not, load it first
          const artifact = get().projects[projectId]?.artifacts?.[artifactId];
          if (!artifact) {
            console.log(`[updateArtifactStage] Artifact ${artifactId} not in store, loading project ${projectId} first`);
            await get().loadProject(projectId);
          }

          set((state) => {
            const artifact = state.projects[projectId]?.artifacts?.[artifactId];
            if (!artifact) {
              console.error(`[updateArtifactStage] Artifact ${artifactId} still not found after loading project`);
              return state;
            }

            return {
              projects: {
                ...state.projects,
                [projectId]: {
                  ...state.projects[projectId],
                  artifacts: {
                    ...state.projects[projectId]?.artifacts,
                    [artifactId]: {
                      ...artifact,
                      stages: {
                        ...artifact.stages,
                        [stage]: {
                          stage,
                          content,
                          isDirty,
                          updatedAt: new Date().toISOString()
                        }
                      },
                      updatedAt: new Date().toISOString()
                    }
                  }
                }
              }
            };
          });
        } catch (error) {
          console.error('Failed to update artifact stage:', error);
          throw error;
        }
      },

      deleteArtifact: async (projectId: string, artifactId: string) => {
        try {
          await api.delete(`/projects/${projectId}/artifacts/${artifactId}`);

          set((state) => {
            const { [artifactId]: removed, ...remainingArtifacts } = state.projects[projectId]?.artifacts || {};
            return {
              projects: {
                ...state.projects,
                [projectId]: {
                  ...state.projects[projectId],
                  artifacts: remainingArtifacts
                }
              },
              ui: {
                ...state.ui,
                selectedArtifactId: state.ui.selectedArtifactId === artifactId ? null : state.ui.selectedArtifactId
              }
            };
          });
        } catch (error) {
          console.error('Failed to delete artifact:', error);
          throw error;
        }
      },

      setSelectedArtifact: (artifactId: string | null) => {
        set((state) => ({
          ui: {
            ...state.ui,
            selectedArtifactId: artifactId
          }
        }));
      },

      // ======================================================================
      // Validation Actions
      // ======================================================================

      validateProject: async (projectId: string) => {
        set((state) => ({
          ui: {
            ...state.ui,
            isValidating: true
          }
        }));

        try {
          const response = await api.post(`/projects/${projectId}/validate`);
          const { validation } = response.data;

          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...state.projects[projectId],
                metadata: {
                  ...state.projects[projectId]?.metadata,
                  lastValidatedAt: new Date().toISOString()
                }
              }
            },
            ui: {
              ...state.ui,
              isValidating: false
            }
          }));

          return validation;
        } catch (error) {
          console.error('Failed to validate project:', error);
          set((state) => ({
            ui: {
              ...state.ui,
              isValidating: false
            }
          }));
          throw error;
        }
      },

      validateArtifact: async (projectId: string, artifactId: string) => {
        try {
          const response = await api.post(`/projects/${projectId}/artifacts/${artifactId}/validate`);
          const { validation } = response.data;

          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...state.projects[projectId],
                artifacts: {
                  ...state.projects[projectId]?.artifacts,
                  [artifactId]: {
                    ...state.projects[projectId]?.artifacts?.[artifactId],
                    validation
                  }
                }
              }
            }
          }));

          return validation;
        } catch (error) {
          console.error('Failed to validate artifact:', error);
          throw error;
        }
      },

      // ======================================================================
      // Library Actions
      // ======================================================================

      saveToLibrary: async (projectId: string) => {
        set((state) => ({
          ui: {
            ...state.ui,
            isSaving: true
          }
        }));

        try {
          await api.post(`/projects/${projectId}/save-to-library`);

          set((state) => ({
            projects: {
              ...state.projects,
              [projectId]: {
                ...state.projects[projectId],
                status: 'LIBRARY',
                metadata: {
                  ...state.projects[projectId]?.metadata,
                  updatedAt: new Date().toISOString()
                }
              }
            },
            ui: {
              ...state.ui,
              isSaving: false
            }
          }));
        } catch (error) {
          console.error('Failed to save to library:', error);
          set((state) => ({
            ui: {
              ...state.ui,
              isSaving: false
            }
          }));
          throw error;
        }
      },

      copyToWorkbench: async (projectId: string) => {
        try {
          const response = await api.post(`/projects/${projectId}/copy-to-workbench`);
          const { newProjectId } = response.data;

          // Load the new project
          await get().loadProject(newProjectId);

          return newProjectId;
        } catch (error) {
          console.error('Failed to copy to workbench:', error);
          throw error;
        }
      },

      // ======================================================================
      // Utility
      // ======================================================================

      getActiveProject: () => {
        const { activeProjectId, projects } = get();
        if (!activeProjectId) return null;
        return projects[activeProjectId] || null;
      },

      getSelectedArtifact: () => {
        const { activeProjectId, projects, ui } = get();
        if (!activeProjectId || !ui.selectedArtifactId) return null;
        return projects[activeProjectId]?.artifacts?.[ui.selectedArtifactId] || null;
      },

      getArtifactsByType: (projectId: string, type: ArtifactType) => {
        const { projects } = get();
        const project = projects[projectId];
        if (!project) return [];

        return Object.values(project.artifacts).filter(
          (artifact) => artifact.type === type
        );
      },

      // ======================================================================
      // UI Actions
      // ======================================================================

      setExplorerGroupBy: (groupBy: 'type' | 'none') => {
        set((state) => ({
          ui: {
            ...state.ui,
            explorerGroupBy: groupBy
          }
        }));
      },

      resetStore: () => {
        set({
          activeProjectId: null,
          projects: {},
          libraryProjects: {},
          ui: { ...initialState.ui }
        });
      }
    }),
    {
      name: 'luma-project-storage',
      partialize: (state) => ({
        // Only persist active project ID and UI preferences
        activeProjectId: state.activeProjectId,
        ui: {
          activeStage: state.ui.activeStage,
          explorerGroupBy: state.ui.explorerGroupBy
        }
      })
    }
  )
);
